<template>
  <div class="AppActionBar">
    <ul v-resize="setActionBarMoreMenuItems" class="AppActionBar-list">
      <li class="AppActionBar-list-item AppActionBar-list-item--selectionLabel">
        <AppActionBarSelectionLabel 
          :selectedItemsLabelPath="selectedItemsLabelPath"
          :totSelectedDocuments="totSelectedDocuments"
          @clear-selection="onClearSelection"
        />
      </li>
      <slot></slot>
    </ul>
    <AppActionBarMoreActionsMenu v-if="moreActionsMenuItems" :menu="moreActionsMenuItems" />
  </div>
</template>

<script>
import { debounce } from 'lodash-es'

import AppActionBarMoreActionsMenu from '@/common/app-action-bar/AppActionBarMoreActionsMenu.vue'
import AppActionBarSelectionLabel from '@/common/app-action-bar/AppActionBarSelectionLabel.vue'
import { handleElementDimensionResize } from '@/common/utils/dimensionObserver'

// This duration is meant to wait after any drawer width change so that
// we can trigger a refresh to update the v-content position relative to the new drawer width
// This is due to a Vuetify bug when the navigation drawer has multiple width values set to it
// Especially when using different units
const CONTENT_REFLOW_DELAY = 190

export default {
  name: 'AppActionBar',
  components: {
    AppActionBarMoreActionsMenu,
    AppActionBarSelectionLabel,
  },
  props: {
    menuItems: {
      type: Array,
      required: true,
    },
    selectedItems: {
      type: Array,
      required: false,
      default: () => [],
    },
    selectedItemsLabelPath: {
      type: String,
      required: false,
    },
  },
  data () {
    return {
      menuItemsDisplayed: [],
      resizeObserverDestroyCallback: null,
    }
  },
  created () {
    this.setActionBarMoreMenuItems = debounce(this.setActionBarMoreMenuItems, 250, { leading: true })
  },
  mounted () {
    this.setActionBarMoreMenuItems()
    this.resizeObserverDestroyCallback = handleElementDimensionResize('.AppActionBar', 'width', (newWidth) => {
      if (newWidth) {
        this.setActionBarMoreMenuItems()
      }
    })
  },
  computed: {
    moreActionsMenuItems () {
      return this.menuItems.filter((item) => this.menuItemsDisplayed.includes(item.name))
    },
    totSelectedDocuments () {
      return this.selectedItems.length
    },
  },
  methods: {
    onClearSelection () {
      this.$emit('clear-selection')
    },
    async setActionBarMoreMenuItems () {
      await this.$nextTick()

      setTimeout(() => {
        const actionBar = document.getElementsByClassName('AppActionBar-list')[0]

        if (actionBar?.children) {
          const actionBarItems = Array.from(actionBar.children)
          const actionBarOffsetTop = actionBar.offsetTop

          actionBarItems.forEach(item => {
            const itemName = item.getAttribute('name')
            const itemIndex = this.menuItems.findIndex((item) => item.name === itemName)
            const isDisplayed = this.menuItemsDisplayed.includes(itemName)

            if (item.offsetTop > actionBarOffsetTop && itemName) {
              if (!isDisplayed) {
                this.menuItemsDisplayed.push(itemName)
              }
            } else if (itemIndex !== -1) {
              if (isDisplayed) {
                this.menuItemsDisplayed = this.menuItemsDisplayed.filter(itemDisplayedName => itemDisplayedName !== itemName)
              }
            }
          })
        }
      }, CONTENT_REFLOW_DELAY)
    },
  },
  watch: {
    totSelectedDocuments (value) {
      if (value) {
        this.setActionBarMoreMenuItems()
      }
    },
  },
  beforeUnmount () {
    if (this.resizeObserverDestroyCallback) {
      this.resizeObserverDestroyCallback()
    }
  },
}
</script>

<style scoped lang="scss">
.AppActionBar {
  display: flex;
  align-items: center;
  padding: 6px 8px;
  background-color: white;
  border: 2px solid rgb(var(--v-theme-darkblue-300));
  border-radius: 12px;
}

.AppActionBar-list {
  align-items: stretch;
  display: flex;
  flex-basis : 0;
  flex-grow : 1;
  flex-shrink : 1;
  flex-wrap: wrap;
  height: 40px;
  overflow: hidden;
  padding: 0;
}

:deep(.AppActionBar-list-item) {
  list-style-type: none;
  display: inline-block;
}

:deep(.AppActionBar-list-item--selectionLabel) {
  display: flex;
  margin-right: 16px;
  min-height: 40px;
  
  & + .AppActionBar-list-item:before {
    background-color: rgb(var(--v-theme-grey-lighten-4));
    content: '';
    display: flex;
    height: 24px;
    margin-top: 8px;
    position: absolute;
    vertical-align: middle;
    width: 1px;
  }
}
</style>
